import React, { useState, useEffect } from 'react';
import { Camera, Play, MapPin, ChevronLeft, ChevronRight, X, Maximize2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { neighborhoodsAPI, videosAPI } from '../utils/api';

// Fallback data for initial state or if API fails
const fallbackNeighborhoods = [
  {
    id: 'waterfront',
    name: 'Waterfront & Marina',
    description: 'Stunning St. Lawrence River views, Lamoureux Park, beaches, and the Cornwall Marina.',
    highlights: ['River beaches', 'Walking trails', 'Marina & boating', 'Sunset views'],
    photos: [
      { id: '1', url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800', caption: 'St. Lawrence River at sunset' },
      { id: '2', url: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800', caption: 'Marina boat docks' },
    ]
  },
];

const fallbackVideos = [
  {
    id: 'riverside-trail',
    title: 'Riverside Trail - Cornwall Ontario',
    description: 'Explore the beautiful waterfront trail along the St. Lawrence River',
    youtube_id: '49o46QIMpLA',
    duration: '2:30',
    category: 'Outdoors'
  },
];

export const ExplorePage = () => {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [neighborhoodsRes, videosRes] = await Promise.all([
          neighborhoodsAPI.getAll(),
          videosAPI.getAll()
        ]);
        
        setNeighborhoods(neighborhoodsRes.data.length > 0 ? neighborhoodsRes.data : fallbackNeighborhoods);
        setVideos(videosRes.data.length > 0 ? videosRes.data : fallbackVideos);
      } catch (error) {
        console.error('Error fetching explore data:', error);
        setNeighborhoods(fallbackNeighborhoods);
        setVideos(fallbackVideos);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const openLightbox = (neighborhood, index) => {
    setSelectedNeighborhood(neighborhood);
    setLightboxIndex(index);
    setLightboxImage(neighborhood.photos[index]);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setSelectedNeighborhood(null);
  };

  const navigateLightbox = (direction) => {
    if (!selectedNeighborhood) return;
    const newIndex = direction === 'next' 
      ? (lightboxIndex + 1) % selectedNeighborhood.photos.length
      : (lightboxIndex - 1 + selectedNeighborhood.photos.length) % selectedNeighborhood.photos.length;
    setLightboxIndex(newIndex);
    setLightboxImage(selectedNeighborhood.photos[newIndex]);
  };

  return (
    <section className="pt-24 pb-20 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen" data-testid="explore-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-blue-100 px-4 py-2 rounded-full mb-4">
            <Camera className="w-5 h-5 text-blue-700" />
            <span className="text-sm font-semibold text-blue-700">Virtual Tour</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
            Explore Cornwall
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Take a visual tour of Cornwall's beautiful neighborhoods, waterfront, and community spaces
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-slate-500">Loading galleries...</div>
          </div>
        ) : (
          <>
            {/* Video Section */}
            {videos.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Play className="w-8 h-8 text-red-600" />
                  Video Tours
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {videos.map((video) => (
                    <Card 
                      key={video.id} 
                      className="border-2 hover:border-blue-300 transition-all overflow-hidden cursor-pointer group"
                      onClick={() => setPlayingVideo(video)}
                      data-testid={`video-${video.id}`}
                    >
                      <div className="relative aspect-video bg-slate-200">
                        <img 
                          src={`https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
                          alt={video.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { 
                            e.target.src = `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`;
                          }}
                        />
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play className="w-8 h-8 text-white ml-1" fill="white" />
                          </div>
                        </div>
                        {video.duration && (
                          <Badge className="absolute bottom-2 right-2 bg-black/70 text-white">
                            {video.duration}
                          </Badge>
                        )}
                        <Badge className="absolute top-2 left-2 bg-blue-600 text-white">
                          {video.category}
                        </Badge>
                      </div>
                      <CardContent className="pt-4">
                        <h3 className="font-bold text-slate-900 mb-1">{video.title}</h3>
                        <p className="text-sm text-slate-600">{video.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Neighborhood Galleries */}
            {neighborhoods.length > 0 && (
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <MapPin className="w-8 h-8 text-emerald-600" />
                  Neighborhood Photo Galleries
                </h2>
                <div className="space-y-12">
                  {neighborhoods.map((neighborhood) => (
                    <Card key={neighborhood.id} className="border-2 overflow-hidden" data-testid={`gallery-${neighborhood.id}`}>
                      <CardHeader className="bg-gradient-to-r from-emerald-50 to-blue-50">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-2xl mb-2">{neighborhood.name}</CardTitle>
                            <p className="text-slate-600">{neighborhood.description}</p>
                            <div className="flex flex-wrap gap-2 mt-3">
                              {neighborhood.highlights.map((highlight, idx) => (
                                <Badge key={idx} variant="outline" className="bg-white">
                                  {highlight}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {neighborhood.photos.map((image, index) => (
                            <div 
                              key={image.id || index}
                              className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                              onClick={() => openLightbox(neighborhood, index)}
                            >
                              <img 
                                src={image.url} 
                                alt={image.caption}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400'; }}
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                                <p className="text-white text-xs">{image.caption}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* CTA */}
        <Card className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 mt-12">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <h3 className="text-2xl font-bold text-slate-900">Ready to See More?</h3>
            <p className="text-slate-600 max-w-xl mx-auto">
              Calculate your potential savings and explore housing options in these beautiful neighborhoods.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                <a href="/cost-calculator">Calculate Savings</a>
              </Button>
              <Button asChild variant="outline" className="border-emerald-400 text-emerald-700">
                <a href="/housing-market">View Housing Market</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>
          
          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2"
            onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          
          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2"
            onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          <div className="max-w-5xl max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxImage.url} 
              alt={lightboxImage.caption}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4 text-lg">{lightboxImage.caption}</p>
            <p className="text-gray-400 text-center text-sm">
              {lightboxIndex + 1} / {selectedNeighborhood?.photos.length} - {selectedNeighborhood?.name}
            </p>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {playingVideo && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPlayingVideo(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
            onClick={() => setPlayingVideo(null)}
          >
            <X className="w-8 h-8" />
          </button>
          
          <div className="w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${playingVideo.youtube_id}?autoplay=1`}
              title={playingVideo.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}
    </section>
  );
};
